import React, { useState, useEffect } from "react";
import { Company } from "../../../../../types/Company";
import { User } from "../../../../../types/User";
import {
    updateCashFlowDetails,
    getUsersByCompany,
    addUser,
    updateCompany,
    handleBudgetLogic
} from "../../../../../services/api";
import "./CompanyItem.css";
import { useChat } from "../../../Layout/ChatProvider/ChatProvider"; // ✅ Import Chat Context
import bankNotCatalogedIcon from "../../../../../assets/bank-not-cataloged.png";
import harigaIcon from "../../../../../assets/hariga-icon.png";
import cardNotCatalogedIcon from "../../../../../assets/card_not_cataloged.png";
import nigrarotTodayIcon from "../../../../../assets/nigrarot_today.png";
import notAdjastedIcon from "../../../../../assets/not_adjusted.png";
import totalNigrarotIcon from "../../../../../assets/total_nigrarot.png";
import refreshIcon from "../../../../../assets/refresh-company_details.png";
import budgetIcon from "../../../../../assets/budget-icon.png";
import editIcon from "../../../../../assets/pencil-edit-icon.png";
import arrowDownIcon from "../../../../../assets/arrow-down.png";
import whatsapp from "../../../../../assets/whatsapp.png";
import EditUserPopup from "./EditUserPopup/EditUserPopup";
import StatusLabel from "./StatusLabel/StatusLabel";
import CreateUserPopup from "./CreateUserPopup/CreateUserPopup";
import EditCompanyModal from "./EditCompanyModal/EditCompanyModal";
import {CompanyStatus} from "../../../../../types/CompanyStatuses";


interface CompanyItemProps {
    company: Company;
    isSelected: boolean;
    onSelect: (company: Company) => void;
}

const CompanyItem: React.FC<CompanyItemProps> = ({ company, isSelected, onSelect }) => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [doneDate, setDoneDate] = useState<Date | null>(company.done_date ? new Date(company.done_date) : null);
    const [detailsUpdatedAt, setDetailsUpdatedAt] = useState<Date | null>(company?.company_details?.updated_at ? new Date(company.company_details.updated_at) : null);
    const [companyDetails, setCompanyDetails] = useState(company.company_details);
    const [isBudgetAllowed, setIsBudgetAllowed] = useState(company.is_budget_allow);
    const [showConfirm, setShowConfirm] = useState(false);
    const [harigaText, setHarigaText] = useState("");
    const { users, fetchUsersByCompany, fetchMessages } = useChat();
    const [showUsers, setShowUsers] = useState(false);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [isCreateUserPopupOpen, setIsCreateUserPopupOpen] = useState(false); // State to control CreateUserPopup visibility
    const [companyId, setCompanyId] = useState<number | null>(null); // New state to store company ID
    const [isEditCompanyModalOpen, setIsEditCompanyModalOpen] = useState(false);
    const [companyStatus, setCompanyStatus] = useState(company.status);
    const { operatorUsers } = useChat(); // Access operator users directly from context
    const [statusDescription, setStatusDescription] = useState(company.status_description || ""); // ✅ NEW

    // ✅ Truncate today's date to remove time (keep only YYYY-MM-DD)
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const handleDoneClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        // Check if Ctrl (Windows) or Cmd (Mac) is pressed
        const isModifierPressed = event.ctrlKey || event.metaKey;
        
        // Only allow click if either:
        // 1. isDone is false (normal case)
        // 2. isDone is true AND modifier key is pressed
        if (!isDone || (isDone && isModifierPressed)) {
            setShowConfirm(true);
        }
    };

    const operatorUser = operatorUsers.find(
        (user) => user.id === Number(company.operator_user_id)
    );

    useEffect(() => {
        setShowUsers(false);
    }, [isSelected]);

    useEffect(() => {
        const text = getHarigaText();
        setHarigaText(text ?? "");
    }, [companyDetails]);

    // ✅ Check if done_date is greater than today (meaning it's in the future)
    const isDone = doneDate && doneDate.getTime() > today.getTime();

    // Calls updateCashFlowFile API and updates done_date if successful
    const confirmUpdate = async (event: React.MouseEvent) => {
        setShowConfirm(false);
        event.stopPropagation();
        if (isUpdating) return; // ✅ Prevent multiple clicks
        setIsUpdating(true);
        try {
            const response = await updateCashFlowDetails(company.id.toString(), true);
            console.log("✅ Update Response:", response);

            if (!response) {
                alert(`❌ Update failed: ${response}`);
            } else {
                setDoneDate(new Date()); // ✅ Update done_date to now
                setDetailsUpdatedAt(new Date());
                // ✅ Update companyDetails with the new response
                setCompanyDetails(prevDetails => {
                    const newDetails = {
                        ...response, // ✅ Replace with the new response
                        updated_at: new Date().toISOString() // ✅ Ensure proper date format
                    };

                    // ✅ Prevent unnecessary re-renders if the data is unchanged
                    return JSON.stringify(prevDetails) !== JSON.stringify(newDetails) ? newDetails : prevDetails;
                });
            }
            if (company.id) {
                fetchMessages(company.id.toString());
            }
        } catch (error) {
            console.error("❌ Error updating cash flow file:", error);
            alert("❌ Error updating cash flow file: " + error);
        } finally {
            setIsUpdating(false);
        }
    };


    const handleArrowClick = async (event: React.MouseEvent) => {
        event.stopPropagation();
        if (showUsers) {
            setShowUsers(false);
            return;
        }
        setIsLoadingUsers(true);
        if (isSelected) {
            try {
                await fetchUsersByCompany(company.id.toString());
                setShowUsers(true);
            } catch (error) {
                console.error("❌ Error fetching users:", error);
            } finally {
                setIsLoadingUsers(false);
            }
        }
    };

    // Function to determine if the date is before today
    const isDateBeforeToday = (date: Date | null) => {
        if (!date) return false;
        return date.getTime() < today.getTime();
    };

    const getHarigaText = () => {
        if (Number(companyDetails?.total_not_updated ?? 0) > 0) {
            const text = `חשבונות לא מעודכנים ${companyDetails.total_not_updated}`;
            return text;
        }
        if (Number(companyDetails?.is_hariga ?? 0) > 0) {
            const text = `חשבונות בחריגה היום ${companyDetails?.is_hariga}`;
            return text;
        }
        if (companyDetails?.total_hariga_excepted !== null && companyDetails?.date_hariga_excepted) {
            if (companyDetails?.total_hariga_excepted === 0) {
                return null;
            }
            const date = new Date(companyDetails?.date_hariga_excepted).toLocaleDateString("he-IL", {
                day: "2-digit",
                month: "2-digit"
            });
            const text = `צפויה חריגה ב-${companyDetails?.total_hariga_excepted} חשבונות החל מתאריך ${date}`;
            return text;
        }
        return null;
    };

    const handleRefreshClick = async (event: React.MouseEvent) => {
        event.stopPropagation();
        if (isUpdating) return; // ✅ Prevent multiple clicks

        setIsUpdating(true);
        try {
            const response = await updateCashFlowDetails(company.id.toString(), false);
            console.log("✅ Update Response:", response);

            if (!response || !response.updated_at) {
                alert(`❌ Update failed: ${response || "Unknown error"}`);
                return;
            }

            setDetailsUpdatedAt(new Date());
            // ✅ Update companyDetails with the new response
            setCompanyDetails(prevDetails => {
                const newDetails = {
                    ...response, // ✅ Replace with the new response
                    updated_at: new Date().toISOString() // ✅ Ensure proper date format
                };

                // ✅ Prevent unnecessary re-renders if the data is unchanged
                return JSON.stringify(prevDetails) !== JSON.stringify(newDetails) ? newDetails : prevDetails;
            });

        } catch (error) {
            console.error("❌ Error updating cash flow details:", error);
            alert("❌ Error updating cash flow file: " + (error instanceof Error ? error.message : "Unknown error"));
        } finally {
            setIsUpdating(false);
        }
    };

    const handleAddUserClick = () => {
        setCompanyId(Number(company.id)); // Set the company ID
        setIsCreateUserPopupOpen(true); // Open the CreateUserPopup
    };

    const handleCreateUserConfirm = async (userData: { firstName: string; lastName: string; phone: string; role: string, isOperator: boolean, receiveVoice: boolean }) => {
        if (companyId === null) {
            console.error("Company ID is required to create a user");
            return;
        }

        // Call the addUser function to send the data to the backend
        const result = await addUser({
            firstName: userData.firstName,
            lastName: userData.lastName,
            phone: userData.phone,
            role: userData.role,
            companyId: companyId, // Pass the companyId
            isVoiceMessage: userData.receiveVoice,
            isMessageContact: userData.isOperator
        });

        if (result.success) {
            console.log("User added successfully!");
            setIsCreateUserPopupOpen(false); // Close the popup after successful user creation
        } else {
            console.error("Failed to add user:", result.message);
        }
    };

    const handleStatusChange = async (newStatus: CompanyStatus, companyId: number) => {
        try {
            if (newStatus.operator_user_id) {
                console.log("KAKAKAKA", company.status_description)
                const result = await updateCompany({
                    id: companyId,
                    status: newStatus,
                    status_description: company.status_description,
                    operator_user_id: newStatus.operator_user_id.toString()
                });
                if (result.success) {
                    setCompanyStatus(newStatus); // ✅ Update the local state
                } else {
                    console.error("❌ Failed to update company status:", result.message);
                }
            } else {
                console.log("KIKIKI")
                const result = await updateCompany({
                    id: companyId,
                    status: newStatus,
                });
                if (result.success) {
                    setCompanyStatus(newStatus); // ✅ Update the local state
                } else {
                }
            }
        } catch (error) {
            console.error("❌ Error updating company status:", error);
        }
    };


    const handleStatClick = (statType: string) => {
        if (!company.company_details) {
            console.warn("company_details is undefined for company ID:", company.id);
            return;
        }
        console.log(`Clicked on: ${statType} for company ID: ${company.id}`);

        let url = "";

        switch (statType) {
            case "פעולות לא מותאמות":
                url = `https://bsecure.bizibox.biz/cfl/cash-flow/bankmatch/bank?id=${company.organization_number}`;
                break;
            case "סה\"כ נגגרות":
                url = `https://bsecure.bizibox.biz/cfl/cash-flow/bankmatch/bank?id=${company.organization_number}`;
                break;
            case "נגררות היום":
                url = `https://bsecure.bizibox.biz/cfl/cash-flow/bankmatch/bank?id=${company.organization_number}`;
                break;
            case "פעולות אשראי לא מקוטלגות":
                url = `https://bsecure.bizibox.biz/cfl/financialManagement/creditsCard/details?id=${company.organization_number}`;
                break;
            case "פעולות בנק לא מקוטלגות":
                url = `https://bsecure.bizibox.biz/cfl/financialManagement/bankAccount/details?id=${company.organization_number}`;
                break;
            default:
                break;
        }
        window.open(url, "_blank");
        console.log("Badge bank_not_cataloged:", company.company_details.bank_not_cataloged);
    };

    const handleEditCompanyClick = (event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent the click from bubbling up to the company item
        setIsEditCompanyModalOpen(true);
    };

    useEffect(() => {
        setStatusDescription(company.status_description || ""); // ✅ make sure it's always set
    }, [company.status_description]);

    // Handles the budget logic button click with error handling and feedback
    const handleBudgetLogicClick = async (event: React.MouseEvent) => {
        event.stopPropagation();
        try {
            const result = await handleBudgetLogic(company.id.toString());
            if (!result.success) {
                alert('❌ Budget logic handling failed.');
            } else {
                console.log('✅ Budget logic handled successfully.');
            }
        } catch (error) {
            console.error("❌ Error handling budget logic:", error);
            alert('❌ An error occurred while handling budget logic.');
        }
    };

    const handleEditCompanyConfirm = async (companyData: {
        name: string;
        id: string;
        occupation: string;
        billing_amount: string;
        status_description: string;
    }) => {
        setStatusDescription(companyData.status_description);
        // The actual API call is handled in the EditCompanyModal component
        // Here we just need to update the local state if needed
        console.log("Company updated successfully:", companyData);
        // You might want to refresh the company list or update the local state here
    };

    return (
        <div className={`company-item ${isSelected ? "active" : ""}`} onClick={() => onSelect(company)}>
            <div className="company-name-date-refresh-container">
                <div className="company-name-date-refresh-container-widget">
                    <div className="company-name-and-arrow-container">
                        <button className="arrow-down-btn" onClick={handleArrowClick}>
                            <div className="icon-arrow-down">
                                <img
                                    src={arrowDownIcon}
                                    alt={"expand users"}
                                    className={`icon-arrow-down-image ${showUsers ? "rotate" : ""}`}
                                />
                            </div>
                        </button>
                        <div className="edit-company-btn-and-name-container">
                            <button className="edit-company-btn" onClick={handleEditCompanyClick}>
                                <img src={editIcon} alt="edit company" className="edit-icon" />
                            </button>
                            <span className="company-name">{company.name}</span>
                        </div>
                    </div>
                    <div className="company-widgets-container">
                        <div className="company-date-refresh" >
                            <div className="updated-at" style={{ color: isDateBeforeToday(detailsUpdatedAt) ? "#E48375" : "inherit" }}>
                                {detailsUpdatedAt && (
                                    <>
                                        {detailsUpdatedAt.toLocaleTimeString("he-IL", {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                            hour12: false
                                        })}{" "}
                                        |{" "}
                                        {detailsUpdatedAt.toLocaleDateString("he-IL", {
                                            day: "2-digit",
                                            month: "2-digit",
                                            year: "numeric"
                                        })}
                                    </>
                                )}
                            </div>
                            <button className={`refresh-button-icon ${isUpdating ? "spinning" : ""}`} onClick={handleRefreshClick} disabled={isUpdating}>
                                <img src={refreshIcon} alt={"Refresh"} className="icon-refresh-image" />
                            </button>
                            {isBudgetAllowed && (
                                <button className="budget-button-icon" onClick={handleBudgetLogicClick}>
                                    <img src={budgetIcon} alt={"Refresh"} className="icon-budget-image" />
                                </button>
                            )}
                        </div>
                        <div className="labels-and-roles-container">
                            <div className="role-container">
                                {operatorUser ? `${operatorUser.first_name} ${operatorUser.last_name}` : "ללא מנהל תזרים"}
                                {/* Pass the full companyStatus object */}
                                {companyStatus ? (
                                    <StatusLabel
                                        companyStatus={company.status} // Pass companyStatus object
                                        companyId={Number(company.id)}
                                        onChange={handleStatusChange}
                                    />
                                ) : (
                                    <div>לא נמצא סטטוס</div>
                                )}
                            </div>
                        </div>
                    </div>
                    {statusDescription && (
                        <div className="company-description">
                            {statusDescription}
                        </div>
                    )}
                </div>
            </div>
            {showUsers && (
                <div className={`users-list ${showUsers ? "expand" : ""}`}>
                    <button className="add-user-button" onClick={handleAddUserClick} disabled={isUpdating}>
                        <span className="add-user-text">הוספת משתמש חדש</span>
                        <span className="add-user-icon">+</span>
                    </button>
                    {isLoadingUsers ? (
                        <div className="loading-users">טוען משתמשים...</div>
                    ) : users.length > 0 ? (
                        users
                            .filter((user) => user.job_title_id === "לקוח")
                            .map((user) => (
                                <div key={user.id} className="user-item">
                                    <button className="user-whatsapp-button" onClick={handleArrowClick}>
                                    <img
                                            src={whatsapp}
                                            alt={"whatsapp user"}
                                            className={`icon-whatsapp-user`}
                                        />
                                    </button>
                                    <div
                                        className="user-alias clickable"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedUser(user);
                                            setIsPopupOpen(true);
                                        }}
                                    >
                                        {user.first_name} {user.last_name} | {user.job_title_id}
                                    </div>
                                </div>
                        ))
                    ) : (
                        <div className="no-users">לא נמצאו משתמשים</div>
                    )}
                </div>
            )}
            {getHarigaText() && (
                <div className="hariga-container">
                    <span className="hariga-text">{getHarigaText()}</span>
                    <img src={harigaIcon} alt="warning" className="hariga-icon" />
                </div>
            )}
            <div className="line-div"></div>
            <div className="buttons-container">
                <button
                    className={`done-btn ${isUpdating ? "" : isDone ? "done-green" : ""}`}
                    onClick={handleDoneClick}
                    disabled={isUpdating}
                >
                    {isUpdating
                        ? "...מעדכן"
                        : isDone
                            ? "התפעול הסתיים"
                            : "בתפעול"}
                </button>
                <div className="icon-buttons-container">
                    {[
                        { icon: notAdjastedIcon, value: companyDetails?.not_adjusted || 0, label: "פעולות לא מותאמות" },
                        { icon: totalNigrarotIcon, value: companyDetails?.total_nigrarot || 0, label: "סה\"כ נגגרות" },
                        { icon: nigrarotTodayIcon, value: companyDetails?.nigrarot_today || 0, label: "נגררות היום" },
                        { icon: cardNotCatalogedIcon, value: companyDetails?.card_not_cataloged || 0, label: "פעולות אשראי לא מקוטלגות" },
                        { icon: bankNotCatalogedIcon, value: companyDetails?.bank_not_cataloged || 0, label: "פעולות בנק לא מקוטלגות" }
                    ].map((item, index) => (
                        <div key={index} className="tooltip-container">
                            <button className="icon-btn" onClick={() => handleStatClick(item.label)}>
                                <div className="icon-container">
                                    <img src={item.icon} alt={item.label} className="icon-image" />
                                    {item.value !== null && item.value > 0 && (
                                        <span className="notification-badge">{item.value}</span>
                                    )}
                                </div>
                            </button>
                            <span className="tooltip-text">{item.label}</span> {/* Tooltip */}
                        </div>
                    ))}
                </div>
                {showConfirm && (
                    <div className="modal-overlay">
                        <div className="modal-content">
                            <p>.האם אתה בטוח? הודעות ישלחו ללקוח</p>
                            <div className="modal-buttons">
                                <button className="confirm-btn" onClick={confirmUpdate}>כן</button>
                                <button className="cancel-btn" onClick={() => setShowConfirm(false)}>ביטול</button>
                            </div>
                        </div>
                    </div>
                )}
                {isPopupOpen && selectedUser && (
                    <EditUserPopup
                        user={selectedUser} // Passing the selected user object
                        userId={selectedUser.id.toString()} // Pass userId separately if needed
                        onClose={() => setIsPopupOpen(false)}
                        onConfirm={(userData) => {
                            console.log("✅ User updated:", userData, "for userId:", selectedUser.id);
                            setIsPopupOpen(false);
                        }}
                    />
                )}

                {isCreateUserPopupOpen && (
                    <CreateUserPopup
                        companyId={companyId} // Pass the companyId to the popup
                        onClose={() => setIsCreateUserPopupOpen(false)}
                        onConfirm={handleCreateUserConfirm}
                    />
                )}
            </div>

            {isEditCompanyModalOpen && (
                <EditCompanyModal
                    onClose={() => setIsEditCompanyModalOpen(false)}
                    onConfirm={handleEditCompanyConfirm}
                    companyId={company.id}
                    initialData={{
                        name: company.name,
                        id: company.organization_number,
                        occupation: company.occupation,
                        billing_amount: company.billing_amount,
                        operator_user_id: company.operator_user_id,
                        status_description: company.status_description,
                    }}
                />
            )}
        </div>
    );
};

export default CompanyItem;